import {AxiosRequestConfig} from 'axios';
import {FieldValues, UseFormSetError} from 'react-hook-form';
import {IId, IUserFormValues} from '@/interfaces';
import {IABTestValues} from '@/queries/abTests/interfaces';
import {IContactInfo, IFaqSection} from '@/queries/help/interfaces';
import {IRegisteredHost} from '@/queries/hosts/interfaces';

export enum ORDER_PARAMS {
    desc = 'desc',
    asc = 'asc'
}

export enum ANTD_ORDERS {
    desc = 'descend',
    asc = 'ascend'
}

export enum PER_PAGE {
    creatives = 5,
    abTests = 6,
    landingQueries = 8,
    articles = 8,
    traffic = 8,
    profitability = 8,
    hosts = 8,
    monitoring = 10,
    users = 10,
    reviews = 12,
    overviewStats = 14,
    autoComplete = 100,
    all = -1
}

export enum REVIEW_TYPES {
    article = 'article',
    creative = 'creative'
}

export enum REVIEW_STATUS {
    pending = 'pending',
    approved = 'approved',
    rejected = 'rejected'
}

export enum USER_ROLES {
    admin = 'administrator',
    partner = 'sm_pc_partner',
    nonVerified = 'sm_pc_partner_non_verified',
    publisher = 'publisher'
}

export enum REMOTE_USER_ROLES {
    administrator = 'administrator',
    system = 'system'
}

export enum MONITORING_AGGREGATION {
    date = 'date',
    week = 'week',
    month = 'month'
}

export enum MONITORING_REVENUE_BY {
    partner = 'partner',
    source = 'source',
    domain = 'domain'
}

export enum MONITORING_PROFITABILITY_BY {
    source = 'source',
    domain = 'domain',
    geo = 'country_code'
}

export enum HOSTS_FEEDS {
    sedo = 'sedo',
    compado = 'compado'
}

export enum ABTEST_STATUS {
    draft = 1,
    active = 2,
    inactive = 3
}

export interface IUseHandleGeneralErrors {
    error: unknown;
    message?: string | null;
}

export interface IUseHandleBackendErrors<TFormValues extends FieldValues> {
    error: unknown;
    setError: UseFormSetError<TFormValues>;
}

export interface IRequest extends AxiosRequestConfig {
    _fields?: string | string[];
    _embed?: string | string[];
}

export interface IRequestParams {
    [key: string]: string | number | string[] | undefined;
}

export interface IById extends IRequest {
    id?: IId;
}

export interface IArticleLibraryFilters {
    author?: string[];
    hosts?: string[];
    label?: string[];
    categories?: string[];
}

export interface IGetArticles extends IRequest {
    page?: number;
    search?: string;
    order?: ORDER_PARAMS;
    orderBy?: string;
    filters?: IArticleLibraryFilters;
}

export interface IGetGeneration extends IRequest {
    query?: string;
    language?: string;
    headline?: string;
    publishTo?: string | string[];
    prompt?: string;
    id?: IId;
    post_label?: string;
}

export interface IGetArticleVerticals extends IRequest {
    search?: string;
}

export interface IGetArticleLabels extends IRequest {
    author?: IId;
    search?: string;
}

export interface IPostArticle extends IRequest {
    id?: IId;
    data: object;
}

export interface IPatchArticle extends IRequest {
    id: IId;
    data: {
        title?: string;
        content?: string;
        featuredImage?: {
            id?: IId | null;
            url?: string | null;
        };
        sm_pc_vertical?: string[];
    };
}

export interface ICreativeLibraryFilters {
    author?: string[];
}

export interface IGetCreativeLabels extends IRequest {
    search?: string;
}

export interface IPostCreative extends IRequest {
    data: {
        label: string;
        attachment: IId;
    };
}

export interface IUploadMedia extends IRequest {
    data: File;
}

export interface IReviewsFilters {
    author?: string[];
    type?: string;
    status?: string;
    label?: string[];
}

export interface IGetReviews extends IRequest {
    page?: number;
    order?: ORDER_PARAMS;
    orderBy?: string;
    search?: string;
    filters?: IReviewsFilters;
}

export interface IGetReviewsOverview extends IGetReviews {
    author?: IId;
}

export interface IPatchReview extends IRequest {
    id?: IId;
    data: {
        review: {
            status: REVIEW_STATUS;
            comment?: string;
            publishTo?: {
                [key: string]: {
                    author: {
                        id: IId;
                    };
                };
            };
        };
    };
}

export interface IMonitoringFilters {
    startDate?: string;
    endDate?: string;
    partner?: string[];
    article?: string[];
    keyword?: string[];
    customId?: string[];
    platformType?: string[];
    countryCode?: string[];
    domain?: string[];
    source?: string[];
    terms?: string[];
}

export type IMonitoringFiltersKeys = keyof Omit<IMonitoringFilters, 'startDate' | 'endDate'>;

export interface IWithMonitoringFilters extends IRequest {
    aggregation?: MONITORING_AGGREGATION;
    filters: IMonitoringFilters;
}

export interface IGetMonitoring extends IRequest {
    page?: number;
    order?: ORDER_PARAMS;
    orderBy?: string;
    partner_id?: number;
    startDate?: string;
    endDate?: string;
    filters?: IMonitoringFilters;
}

export interface IGetMonitoringTraffic extends IRequest {
    type: MONITORING_REVENUE_BY;
    params?: IGetMonitoring;
}

export interface IGetMonitoringProfitability extends IGetMonitoring {
    grouping: MONITORING_PROFITABILITY_BY[];
}

export interface IGetMonitoringFilters extends IRequest {
    startDate?: string;
    endDate?: string;
}

export interface ICurrentUser extends IRequest {
    loginParams?: object;
}

export interface ISignIn extends IRequest {
    data: {
        user_login: string;
        user_password: string;
        remember: boolean;
    };
}

export interface ISignUp extends IRequest {
    data: {
        user_email: string;
    };
}

export interface IRetrievePassword extends IRequest {
    data: {
        user_login: string;
    };
}

export interface IResetPassword extends IRequest {
    data: {
        user_login?: string;
        reset_key?: string;
        new_password: string;
    };
}

export interface IUserFilters {
    role?: USER_ROLES;
    preferredHosts?: string[];
}

export interface IGetUsersAutoComplete extends IRequest {
    search?: string;
}

export interface IGetUsers extends IRequest {
    page?: number;
    search?: string;
    order?: ORDER_PARAMS;
    orderBy?: string;
    filters?: IUserFilters;
}

export interface IPatchUser extends IById {
    data: IUserFormValues;
}

export interface IPostUser extends IRequest {
    data: IUserFormValues;
}

export interface IHostsFilters {
    feed?: HOSTS_FEEDS;
}

export interface IGetHosts extends IRequest {
    page?: number;
    search?: string;
    order?: ORDER_PARAMS;
    orderBy?: string;
    filters?: IHostsFilters;
}

export interface IConnectHost extends IRequest {
    url: string;
    hostFeed?: HOSTS_FEEDS;
}

export interface IRegisterNewHost extends IRequest {
    data: {
        host: string | null;
        hostFeed: HOSTS_FEEDS;
        auth: {
            type: 'WordPress';
            username: string | null;
            applicationPassword: string | null;
        };
    };
}

export interface IPatchRegisteredHost extends IRequest {
    id: IId;
    data: IRegisteredHost;
}

export interface IRegisteredHostAction extends IRequest {
    host: string;
}

export interface IGetRegisteredHostAction extends IRegisteredHostAction {
    search?: string;
    roles?: REMOTE_USER_ROLES;
}

export interface IRegisteredHostAddAction extends IRegisteredHostAction {
    data: {
        name: string;
    };
}

export interface IPostRemoteAccess extends IById {
    data: object;
}

export interface IDeleteRemoteAccess extends IById {
    host: string;
}

export interface IPatchFaq extends IRequest {
    data: {sections: IFaqSection[]};
}

export interface IPatchContact extends IRequest {
    data: IContactInfo;
}

export interface IABTestsFilters {
    host?: string[];
    status?: ABTEST_STATUS;
}

export interface IGetABTests extends IRequest {
    page?: number;
    search?: string;
    filters?: IABTestsFilters;
}

export interface IPostABTest extends IRequest {
    data: Omit<IABTestValues, 'articlesForShow'>;
}

export interface IPatchABTest extends IById {
    data: Omit<IABTestValues, 'articlesForShow'>;
}

export interface IPatchABTestStatus extends IById {
    data: {
        status: ABTEST_STATUS;
    };
}
